<template>
  <article class="product">
    <header class="product-header">
      <b-row class="mb-5">
        <b-col :[$root.md.key]="7">
          <div class="product-description header-section">
            <div class="eyecatcher-stack">
              <slot name="eyecatcher" />
            </div>

            <div class="description-images" @click="carouselImageClicked">
              <c-carousel
                ref="ImagesCarousel"
                class="images-carousel"
                :arrows="$root.isDesktop"
                asNavFor="ImagesNavigation"
                :autoplay="false"
                :dots="!$root.isDesktop"
                focusOnSelect
                :infinite="false"
                @carousel:init="imagesCarouselChange"
                @afterChange="imagesCarouselChange"
              >
                <slot name="images">
                  <aspect-ratio ratio="4x3" class="is-fallback-image">
                    <b-icon icon="images" />
                  </aspect-ratio>
                </slot>
              </c-carousel>

              <c-carousel
                v-show="$slots.images && $root.isDesktop"
                ref="ImagesNavigation"
                class="images-navigation"
                asNavFor="ImagesCarousel"
                :autoplay="false"
                :dots="false"
                focusOnSelect
                :infinite="false"
                :swipe="false"
                :slidesToShow="6"
                @carousel:init="imagesNavigationInit"
              >
                <slot name="images">
                  <aspect-ratio ratio="4x3" class="is-fallback-image">
                    <b-icon icon="images" />
                  </aspect-ratio>
                </slot>
              </c-carousel>
            </div>
            <LightBox :media="lightBoxMedias" :show-light-box="false" ref="lightBox" :interface-hide-time="120000"></LightBox>

            <c-read-more class="description-text" :translationsPath="`${tPath}.readmore`">
              <slot name="description" />
            </c-read-more>
          </div>
        </b-col>

        <b-col :[$root.md.key]="5">
          <div class="product-title header-section">
            <slot name="title" />
            <div class="title-links"><slot name="links" /></div>
          </div>

          <div class="product-configurations header-section">
            <slot name="configurations" />
          </div>

          <div class="header-section">
            <div class="product-informations">
              <slot name="informations" />
            </div>

            <div class="product-actions">
              <slot name="actions" />

              <div class="shareing-is-caring">
                <b-button :id="`PopoverTarget_${_uid}`" variant="link" class="share-button"
                  ><b-icon icon="share" /> {{ $t(`${tPath}.share.button`) }}</b-button
                >
                <b-popover :target="`PopoverTarget_${_uid}`" triggers="hover" placement="rightbottom">
                  <slot name="share" />
                </b-popover>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </header>

    <slot />
  </article>
</template>

<script>
import AspectRatio from '@/components/private/AspectRatio'
import LightBox from 'vue-it-bigger'

export default {
  name: 'Product',
  components: {
    AspectRatio,
    LightBox
  },
  computed: {
    lightBoxMedias() {
      if (!this.$slots.images) {
        return []
      }

      const images = this.$slots.images.map((vnode) => {
        return {
          thumb: vnode.data.attrs.src,
          src: vnode.data.attrs['data-image-src']
        }
      })

      return images
    }
  },
  methods: {
    imagesCarouselChange(currentIndex) {
      this.$refs.ImagesNavigation.$el.querySelectorAll('.slick-slide').forEach((slide, sIndex) => {
        slide.classList[sIndex === currentIndex ? 'add' : 'remove']('carousel-current')
      })
    },
    imagesNavigationInit() {
      this.$nextTick(() => {
        this.$refs.ImagesNavigation.$el.querySelectorAll('.slick-slide').forEach((slide, sIndex) => {
          slide.addEventListener('click', (e) => {
            this.$refs.ImagesCarousel.$refs.carousel.goTo(sIndex)
          })
        })
      })
    },
    carouselImageClicked(event) {
      const targetImageIndex = event.target.getAttribute('data-image-index')
      if (isNaN(targetImageIndex)) {
        return
      }
      let parentContainer = event.target.parentElement
      while (parentContainer !== null && parentContainer !== this.$refs.ImagesCarousel.$el) {
        parentContainer = parentContainer.parentElement
      }

      if (parentContainer === null) {
        return
      }

      this.$refs.lightBox.showImage(parseInt(targetImageIndex))
    }
  }
}
</script>

<style lang="scss">
@import '~vue-it-bigger/dist/vue-it-bigger.min.css';

// mobile
$product-section-padding-y: $spacer * 1.2 !default;
$product-section-border: map-get($borders, 'bold') !default;

$product-title-links-padding-y: $spacer * 0.25 !default;
$product-title-links-padding-x: $spacer * 2 !default;

$product-description-eyecatcher-gap: $spacer !default;

$product-description-images-carousel-gap: 0 !default;
$product-description-images-navigation-gap: $spacer !default;
$product-description-images-navigation-slide-gap: $spacer * 0.75 !default;
$product-description-images-navigation-slide-img-gap: $spacer * 0.5 !default;
$product-description-images-navigation-slide-img-border: 2px solid transparent !default;
$product-description-images-navigation-slide-current-img-border-color: $primary !default;

$product-description-images-img-fallback-size: 80% !default;
$product-description-images-img-fallback-color: $imgfallback !default;

$product-configuration-fieldset-gap: $spacer !default;
$product-configuration-fieldset-item-gap-y: $product-configuration-fieldset-gap !default;
$product-configuration-fieldset-item-gap-x: $spacer * 2 !default;

$product-informations-gap: 0 !default;

$product-actions-gap: $spacer !default;
$product-actions-buttons-gap: $form-grid-gutter-width !default;
$product-actions-sharing-gap: $spacer !default;
$product-actions-sharing-button-color: $dark !default;

// desktop
$product-breakpoint: $desktop-breakpoint !default;

$product-desktop-section-padding-y: $product-section-padding-y !default;
$product-desktop-section-border: $product-section-border !default;

$product-desktop-title-links-padding-y: $product-title-links-padding-y !default;
$product-desktop-title-links-padding-x: $product-title-links-padding-x !default;

$product-desktop-description-eyecatcher-gap: $product-description-eyecatcher-gap !default;

$product-desktop-description-images-carousel-gap: $spacer !default;
$product-desktop-description-images-navigation-gap: $product-description-images-navigation-gap !default;
$product-desktop-description-images-navigation-slide-gap: $product-description-images-navigation-slide-gap !default;
$product-desktop-description-images-navigation-slide-img-gap: $product-description-images-navigation-slide-img-gap !default;
$product-desktop-description-images-navigation-slide-img-border: $product-description-images-navigation-slide-img-border !default;
$product-desktop-description-images-navigation-slide-current-img-border-color: $product-description-images-navigation-slide-current-img-border-color !default;
$product-desktop-description-images-navigation-slide-current-img-border-color-hover: $gray-300 !default;

$product-desktop-configuration-fieldset-gap: $product-configuration-fieldset-gap !default;
$product-desktop-configuration-fieldset-item-gap-y: $product-configuration-fieldset-item-gap-y !default;
$product-desktop-configuration-fieldset-item-gap-x: $product-configuration-fieldset-item-gap-x !default;

$product-desktop-informations-gap: $product-informations-gap !default;

$product-desktop-actions-gap: $product-actions-gap !default;
$product-desktop-actions-buttons-gap: $product-actions-buttons-gap !default;
$product-desktop-actions-sharing-gap: $product-actions-sharing-gap !default;
$product-desktop-actions-sharing-button-color: $product-actions-sharing-button-color !default;

.product {
  .product-header {
    .header-section {
      margin-top: $product-section-padding-y;
      padding-top: $product-section-padding-y;
      border-top: $product-section-border;
      &:empty {
        display: none;
      }

      &:first-child {
        margin-top: 0;
        padding-top: 0;
        border-top: 0 none;
      }

      + header,
      + section {
        border-top: $product-section-border;
      }
    }

    .product-title {
      h2,
      h3,
      h4,
      h5,
      h6,
      .h2,
      .h3,
      .h4,
      .h5,
      .h6 {
        text-transform: uppercase;
      }

      .lead {
        color: $black;
      }

      .title-links {
        display: flex;
        flex-wrap: wrap;
        margin: ($product-title-links-padding-y * -0.5) ($product-title-links-padding-x * -0.5);

        a {
          display: block;
          padding: ($product-title-links-padding-y * 0.5) ($product-title-links-padding-x * 0.5);
        }
      }
    }

    .product-description {
      position: relative;

      .vib-container {
        background: rgba(255, 255, 255, 1);

        .vib-content {
          .vib-image {
            max-height: 90vh;
          }
        }

        .vib-footer {
          display: none;
        }

        .vib-thumbnail-wrapper {
          bottom: 0;
          top: auto;
          border-top-left-radius: 10px;
          border-top-right-radius: 10px;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;

          .vib-thumbnail,
          .vib-thumbnail-active {
            width: 100px;
            height: 100px;
          }

          .vib-thumbnail-active {
            box-shadow: inset 0 0 0 3px #666;
          }
        }
      }

      .eyecatcher-stack {
        position: absolute;
        top: 0;
        right: 0;
        margin: $product-description-eyecatcher-gap;
        z-index: 2;
      }

      .description-images {
        position: relative;
        z-index: 1;

        .images-carousel {
          @include fullwidth-mobile();
          margin-bottom: $product-description-images-carousel-gap;

          img {
            cursor: zoom-in;
          }
        }

        .images-navigation {
          margin-bottom: $product-description-images-navigation-gap;
          overflow: hidden;

          .slick-list {
            margin-left: $product-description-images-navigation-slide-gap * -0.5;
            margin-right: $product-description-images-navigation-slide-gap * -0.5;

            .slick-slide {
              padding-left: $product-description-images-navigation-slide-gap * 0.5;
              padding-right: $product-description-images-navigation-slide-gap * 0.5;

              img {
                padding-bottom: $product-description-images-navigation-slide-img-gap;
                border-bottom: $product-description-images-navigation-slide-img-border;

                cursor: pointer;
              }

              &.carousel-current {
                img {
                  border-bottom-color: $product-description-images-navigation-slide-current-img-border-color;
                }
              }
            }
          }
        }

        .is-fallback-image {
          .bi {
            display: block;
            position: absolute;
            inset: 0;
            margin: auto;
            width: $product-description-images-img-fallback-size;
            height: $product-description-images-img-fallback-size;
            color: $product-description-images-img-fallback-color;
          }
        }

        &:hover {
          .images-carousel {
            .slick-arrow {
              opacity: 1;
            }
          }
        }
      }

      .description-text {
        @include sr-only();
      }
    }

    .product-configurations {
      fieldset {
        display: flex;
        flex-wrap: wrap;
        margin: ($product-configuration-fieldset-item-gap-y * -0.5) ($product-configuration-fieldset-item-gap-x * -0.5);

        + fieldset {
          padding-top: $product-configuration-fieldset-gap;
        }

        > * {
          padding: ($product-configuration-fieldset-item-gap-y * 0.5) ($product-configuration-fieldset-item-gap-x * 0.5);
        }
      }
    }

    .product-informations {
      margin-top: $product-informations-gap;
    }

    .product-actions {
      margin-top: $product-actions-gap;

      .wishlistbutton,
      .setconfigurationbutton {
        display: block;
        width: 100%;
        margin-top: $product-actions-buttons-gap;
      }

      .shareing-is-caring {
        margin-top: $product-actions-sharing-gap;

        .share-button {
          padding: 0;
          color: $product-actions-sharing-button-color;
          text-decoration: none;
        }

        .social-list {
          justify-content: flex-start;
        }
      }
    }
  }

  @include media-breakpoint-up($product-breakpoint) {
    .product-header {
      .header-section {
        margin-top: $product-desktop-section-padding-y;
        padding-top: $product-desktop-section-padding-y;
        border-top: $product-desktop-section-border;

        &:first-child {
          margin-top: 0;
          padding-top: 0;
          border-top: 0 none;
        }

        + header,
        + section {
          border-top: $product-desktop-section-border;
        }
      }

      .product-title {
        .title-links {
          margin: ($product-desktop-title-links-padding-y * -0.5) ($product-desktop-title-links-padding-x * -0.5);

          a {
            padding: ($product-desktop-title-links-padding-y * 0.5) ($product-desktop-title-links-padding-x * 0.5);
          }
        }
      }

      .product-description {
        .eyecatcher-stack {
          margin: $product-desktop-description-eyecatcher-gap;
        }

        .description-images {
          .images-carousel {
            margin-bottom: $product-desktop-description-images-carousel-gap;
          }

          .images-navigation {
            margin-bottom: $product-desktop-description-images-navigation-gap;

            .slick-list {
              margin-left: $product-desktop-description-images-navigation-slide-gap * -0.5;
              margin-right: $product-desktop-description-images-navigation-slide-gap * -0.5;

              .slick-slide {
                padding-left: $product-desktop-description-images-navigation-slide-gap * 0.5;
                padding-right: $product-desktop-description-images-navigation-slide-gap * 0.5;

                img {
                  padding-bottom: $product-desktop-description-images-navigation-slide-img-gap;
                  border-bottom: $product-desktop-description-images-navigation-slide-img-border;
                }

                &:hover {
                  img {
                    border-bottom-color: $product-desktop-description-images-navigation-slide-current-img-border-color-hover;
                  }

                  &::after {
                    content: '';
                    display: block;
                    position: absolute;
                    top: 0;
                    left: 0;
                    inset: 0;
                    opacity: 0.3;
                    margin-left: $product-desktop-description-images-navigation-slide-gap * 0.5;
                    margin-right: $product-desktop-description-images-navigation-slide-gap * 0.5;
                    margin-bottom: $product-desktop-description-images-navigation-slide-img-gap;
                    background-color: $product-desktop-description-images-navigation-slide-current-img-border-color-hover;
                  }
                }

                &.carousel-current {
                  img {
                    border-bottom-color: $product-desktop-description-images-navigation-slide-current-img-border-color;
                  }
                }
              }
            }
          }
        }

        .description-text {
          @include reset-sr-only();
        }
      }

      .product-configurations {
        fieldset {
          margin: ($product-desktop-configuration-fieldset-item-gap-y * -0.5) ($product-desktop-configuration-fieldset-item-gap-x * -0.5);

          + fieldset {
            padding-top: $product-desktop-configuration-fieldset-gap;
          }

          > * {
            padding: ($product-desktop-configuration-fieldset-item-gap-y * 0.5) ($product-desktop-configuration-fieldset-item-gap-x * 0.5);
          }
        }
      }

      .product-informations {
        margin-top: $product-desktop-informations-gap;
      }

      .product-actions {
        margin-top: $product-desktop-actions-gap;

        .wishlistbutton,
        .setconfigurationbutton {
          margin-top: $product-desktop-actions-buttons-gap;
        }

        .shareing-is-caring {
          margin-top: $product-desktop-actions-sharing-gap;

          .share-button {
            color: $product-desktop-actions-sharing-button-color;
          }
        }
      }
    }
  }
}
</style>
